import { useEffect, useState } from "react";
import api from "../lib/api";

/**
 * useTopConvertingChannel — fetch the lifetime install-attribution
 * summary and return the source with the highest click-to-install
 * conversion (clicks → completed installs).
 *
 * Returns `null` when:
 *  - the summary endpoint is unavailable, or
 *  - no channel has enough click data to be confident (we require
 *    at least CONFIDENCE_FLOOR clicks per channel to consider it).
 *
 * Callers (e.g. the Landing hero) use the result to decide which
 * touchpoint to promote — null = stay neutral, "share"/"qr"/etc.
 * = swap in the corresponding CTA. By gating on a confidence
 * floor we avoid promoting a noisy "1 click, 1 install = 100%"
 * outlier.
 */
const CONFIDENCE_FLOOR = 10; // clicks per channel before we trust the ratio
const PROMOTION_LIFT = 1.5;  // require a 50% lift over the median channel

export default function useTopConvertingChannel() {
  const [top, setTop] = useState(null);
  useEffect(() => {
    let cancelled = false;
    api
      .get("/telemetry/install-attribution/summary?window=lifetime")
      .then((r) => {
        if (cancelled) return;
        const clicked = (r.data && r.data.clicked) || {};
        const completed = (r.data && r.data.completed) || {};
        // Compute conv per source that meets the click floor.
        const ranked = Object.entries(clicked)
          .map(([src, clk]) => {
            const clicks = Number(clk) || 0;
            const inst = Number(completed[src] || 0);
            const conv = clicks > 0 ? inst / clicks : 0;
            return { src, clicks, inst, conv };
          })
          .filter((row) => row.clicks >= CONFIDENCE_FLOOR);
        if (ranked.length < 2) {
          // Need at least two channels to compute a "lift" — otherwise
          // we can't be sure the leader is materially better.
          return;
        }
        ranked.sort((a, b) => b.conv - a.conv);
        const [winner, runnerUp] = ranked;
        if (
          winner.conv > 0
          && winner.conv >= runnerUp.conv * PROMOTION_LIFT
        ) {
          // Cap the displayed conversion at 100% — pre-click-tracking
          // installs can otherwise show >100% which reads as a bug
          // to admins viewing the badge. The internal `conv` ranking
          // is unchanged; only the human-facing %% is clamped.
          setTop({
            src: winner.src,
            conv: Math.min(100, Math.round(winner.conv * 100)),
            clicks: winner.clicks,
            inst: winner.inst,
          });
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);
  return top;
}
